import { analyticsAllowed, embeddedParent, getGuestAnalyticsContext } from './guestAnalytics';
import { rememberAuthAttempt } from './authActivity';
import { UPLOAD_ERROR_CODES } from './uploadRecovery';
import { appBuildVersion } from './clientVersion';
import { productLimitEvent } from './productLimits';
import { pricing } from './pricing';

export const GA_MEASUREMENT_ID = "G-GPHRW6PKGB";
export const META_PIXEL_ID = "1525786295750108";
export const REDDIT_PIXEL_ID = "a2_j67n60etwo3l";

const ANALYTICS_SURFACE = "webapp";
let accountType = 'visitor';
const GUEST_UPGRADE_KEY = 'instanttranscriber_guest_upgrade_analytics';
const GUEST_SAVE_CTA_KEY = 'instanttranscriber_guest_save_cta_v2';
const SAVE_LOCATIONS = new Set(['completed_transcript','after_copy','after_download']);
export function rememberGuestSaveCTA(method, location, offerVariant = 'guest_save_v2') {
  if (!analyticsAllowed() || !['google','email'].includes(method) || !SAVE_LOCATIONS.has(location)) return;
  try { sessionStorage.setItem(GUEST_SAVE_CTA_KEY, JSON.stringify({ method, cta_location: location,
    offer_variant: ['result_context_v3','result_context_v4'].includes(offerVariant) ? offerVariant : 'guest_save_v2', expires: Date.now() + 3600000 })); } catch {}
}
function guestSaveContext(method) {
  try {
    const value = JSON.parse(sessionStorage.getItem(GUEST_SAVE_CTA_KEY) || 'null');
    return value?.expires > Date.now() && value.method === method && SAVE_LOCATIONS.has(value.cta_location)
      ? { cta_location: value.cta_location, offer_variant: ['result_context_v3','result_context_v4'].includes(value.offer_variant) ? value.offer_variant : 'guest_save_v2' } : {};
  } catch { return {}; }
}
const MAX_STRING_LENGTH = 120;
const MAX_GA_EVENT_NAME_LENGTH = 40;
const PREMIUM_ACTIVATION_STORAGE_PREFIX = "instanttranscriber_premium_activated:";
const META_EVENT_STORAGE_PREFIX = "instanttranscriber_meta_event:";
const REDDIT_EVENT_STORAGE_PREFIX = "instanttranscriber_reddit_event:";
const PRODUCT_INTENT_EVENT_STORAGE_PREFIX = "instanttranscriber_product_intent_event:";
const GA_SIGNUP_EVENT_STORAGE_PREFIX = "instanttranscriber_ga_signup_event:";
const CHECKOUT_BEHAVIOR_STORAGE_KEY = "instanttranscriber_checkout_behavior";
const META_ATTRIBUTION_STORAGE_KEY = "instanttranscriber_meta_attribution";
const REDDIT_ATTRIBUTION_STORAGE_KEY = "instanttranscriber_reddit_attribution";
const CHECKOUT_BEHAVIOR_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const META_ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const REDDIT_ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const META_DEFAULT_CURRENCY = "USD";
const META_ATTRIBUTION_MAX_STRING_LENGTH = 500;
const REDDIT_ATTRIBUTION_MAX_STRING_LENGTH = 500;
const CHECKOUT_CONTEXT_EVENT_IGNORE = new Set([
  "page_view",
  "web_vital",
]);
const CHECKOUT_CONTEXT_EVENT_IGNORE_PREFIXES = [
  "begin_checkout",
  "checkout_",
  "premium_activated",
  "select_promotion",
  "upgrade_click",
];

const isBrowser = () => typeof window !== "undefined";

const getGtag = () => {
  if (!isBrowser() || typeof window.gtag !== "function") return null;
  return window.gtag;
};

const getFbq = () => {
  if (!isBrowser() || typeof window.fbq !== "function") return null;
  return window.fbq;
};

const getRdt = () => {
  if (!isBrowser() || typeof window.rdt !== "function") return null;
  return window.rdt;
};

const isMetaPixelEnabled = () => {
  return isBrowser() && window.__instantTranscriberMarketingConsent !== false;
};

const isRedditPixelEnabled = () => {
  return isBrowser() && window.__instantTranscriberMarketingConsent !== false;
};

const cleanValue = (value) => {
  if (value == null) return undefined;
  if (typeof value === "string") return value.slice(0, MAX_STRING_LENGTH);
  if (typeof value === "number" || typeof value === "boolean") return value;
  return String(value).slice(0, MAX_STRING_LENGTH);
};

const cleanParams = (params = {}) => {
  const cleaned = {};
  Object.entries(params).forEach(([key, value]) => {
    const next = cleanValue(value);
    if (next !== undefined) cleaned[key] = next;
  });
  return cleaned;
};

const getStoredCheckoutBehavior = () => {
  if (!isBrowser()) return {};
  try {
    const stored = JSON.parse(localStorage.getItem(CHECKOUT_BEHAVIOR_STORAGE_KEY) || "{}");
    if (!stored || typeof stored !== "object") return {};
    if (stored.updated_at && Date.now() - Number(stored.updated_at) > CHECKOUT_BEHAVIOR_TTL_MS) {
      localStorage.removeItem(CHECKOUT_BEHAVIOR_STORAGE_KEY);
      return {};
    }
    return stored;
  } catch {
    return {};
  }
};

const storeCheckoutBehavior = (updates = {}) => {
  if (!isBrowser()) return;
  const cleaned = cleanParams(updates);
  if (!Object.keys(cleaned).length) return;

  try {
    localStorage.setItem(CHECKOUT_BEHAVIOR_STORAGE_KEY, JSON.stringify({
      ...getStoredCheckoutBehavior(),
      ...cleaned,
      updated_at: Date.now(),
    }));
  } catch {}
};

const rememberCheckoutAppEvent = (eventName) => {
  const event = cleanValue(eventName);
  if (!event || CHECKOUT_CONTEXT_EVENT_IGNORE.has(event)) return;
  if (CHECKOUT_CONTEXT_EVENT_IGNORE_PREFIXES.some((prefix) => event.startsWith(prefix))) return;
  storeCheckoutBehavior({ last_app_event_before_checkout: event });
};

const limitEventValue = (eventName, params = {}) => {
  const event = cleanValue(eventName);
  if (!event) return undefined;
  const detail = cleanValue(params.limit_type || params.reason || params.status);
  return detail ? `${event}:${detail}` : event;
};

export const rememberCheckoutLimitEvent = (eventName, params = {}) => {
  const event = limitEventValue(eventName, params);
  if (!event) return;
  storeCheckoutBehavior({ last_limit_event: event });
};

export const rememberCheckoutUploadedRecord = (recordId) => {
  const id = cleanValue(recordId);
  if (!id) return;
  storeCheckoutBehavior({ last_uploaded_record_id: id });
};

export const getCheckoutBehaviorAttribution = () => {
  const stored = getStoredCheckoutBehavior();
  return cleanParams({
    last_app_event_before_checkout: stored.last_app_event_before_checkout,
    last_limit_event: stored.last_limit_event,
    last_uploaded_record_id: stored.last_uploaded_record_id,
  });
};

const cleanMetaAttributionValue = (value) => {
  if (value == null) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return String(value);
  return String(value).slice(0, META_ATTRIBUTION_MAX_STRING_LENGTH);
};

const cleanRedditAttributionValue = (value) => {
  if (value == null) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return String(value);
  return String(value).slice(0, REDDIT_ATTRIBUTION_MAX_STRING_LENGTH);
};

const readCookie = (name) => {
  if (!isBrowser() || !name) return undefined;
  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!cookie) return undefined;
  try {
    return decodeURIComponent(cookie.slice(prefix.length));
  } catch {
    return cookie.slice(prefix.length);
  }
};

const gaStreamCookieName = () => `_ga_${GA_MEASUREMENT_ID.replace(/^G-/, "")}`;

const gaClientIdFromCookie = (value) => {
  const parts = String(value || "").split(".");
  if (parts.length < 4) return undefined;
  const clientParts = parts.slice(-2);
  if (!clientParts.every((part) => /^\d+$/.test(part))) return undefined;
  return clientParts.join(".");
};

const gaSessionIdFromCookie = (value) => {
  const normalized = String(value || "");
  const gs2Match = normalized.match(/(?:^|[.$])s(\d+)(?:$|\$)/);
  if (gs2Match) return gs2Match[1];

  const parts = normalized.split(".");
  if (parts[0] === "GS1" && /^\d+$/.test(parts[2] || "")) return parts[2];
  return undefined;
};

export const getGoogleAnalyticsAttribution = () => {
  if (!isBrowser()) return {};
  if (window.__instantTranscriberAnalyticsConsent === false) return { ga_tracking_status: 'consent_denied' };
  if (window.instantTranscriberAcquisition) return window.instantTranscriberAcquisition.googleIds();
  return cleanParams({
    ga_client_id: gaClientIdFromCookie(readCookie("_ga")),
    ga_session_id: gaSessionIdFromCookie(readCookie(gaStreamCookieName())),
  });
};

export const resolveGoogleAnalyticsAttribution = async () => {
  if (isBrowser() && window.instantTranscriberAcquisition) {
    return window.instantTranscriberAcquisition.resolveGoogleIds();
  }
  return getGoogleAnalyticsAttribution();
};

export const getAcquisitionContext = () => {
  if (!isBrowser()) return {};
  const context = window.instantTranscriberAcquisition?.context() || {};
  try {
    const stored = JSON.parse(localStorage.getItem('instanttranscriber_auth_attribution') || '{}');
    if (stored.updated_at && Date.now() - stored.updated_at < 90 * 24 * 60 * 60 * 1000) {
      ['landing_path', 'source_intent', 'landing_locale', 'first_touch_source'].forEach(key => {
        if (!context[key] && stored[key]) context[key] = stored[key];
      });
    }
  } catch {}
  return context;
};

const getFbclid = () => {
  if (!isBrowser()) return undefined;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("fbclid") || params.get("meta_fbclid") || undefined;
  } catch {
    return undefined;
  }
};

const getUrlMetaParam = (name) => {
  if (!isBrowser()) return undefined;
  try {
    return new URLSearchParams(window.location.search).get(name) || undefined;
  } catch {
    return undefined;
  }
};

const getUrlRedditParam = (name) => {
  if (!isBrowser()) return undefined;
  try {
    return new URLSearchParams(window.location.search).get(name) || undefined;
  } catch {
    return undefined;
  }
};

const fbcTimestamp = (fbc) => {
  const parts = String(fbc || "").split(".");
  const timestamp = Number(parts[2]);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : undefined;
};

const fallbackFbcFromFbclid = (fbclid, timestamp = Date.now()) => {
  if (!fbclid) return undefined;
  return `fb.1.${timestamp}.${fbclid}`;
};

const getStoredMetaAttribution = () => {
  if (!isBrowser()) return {};
  try {
    const stored = JSON.parse(localStorage.getItem(META_ATTRIBUTION_STORAGE_KEY) || "{}");
    if (!stored || typeof stored !== "object") return {};
    if (stored.updated_at && Date.now() - Number(stored.updated_at) > META_ATTRIBUTION_TTL_MS) {
      localStorage.removeItem(META_ATTRIBUTION_STORAGE_KEY);
      return {};
    }
    return stored;
  } catch {
    return {};
  }
};

const storeMetaAttribution = (attribution = {}) => {
  if (!isBrowser()) return;
  const persistable = {};
  ["meta_fbp", "meta_fbc", "meta_fbclid"].forEach((key) => {
    const value = cleanMetaAttributionValue(attribution[key]);
    if (value !== undefined && value !== "") persistable[key] = value;
  });

  if (!Object.keys(persistable).length) return;

  try {
    localStorage.setItem(META_ATTRIBUTION_STORAGE_KEY, JSON.stringify({
      ...getStoredMetaAttribution(),
      ...persistable,
      updated_at: Date.now(),
    }));
  } catch {}
};

export const captureMetaBrowserAttribution = () => {
  if (!isBrowser()) return {};

  const marketingConsent = isMetaPixelEnabled();
  if (!marketingConsent) {
    return { meta_marketing_consent: false };
  }

  const stored = getStoredMetaAttribution();
  const fbclid = getFbclid() || stored.meta_fbclid;
  const urlFbc = getUrlMetaParam("meta_fbc");
  const storedFbc = stored.meta_fbc;
  const fbc =
    readCookie("_fbc")
    || urlFbc
    || storedFbc
    || fallbackFbcFromFbclid(fbclid, fbcTimestamp(urlFbc || storedFbc));
  const fbp = readCookie("_fbp") || getUrlMetaParam("meta_fbp") || stored.meta_fbp;

  const attribution = Object.fromEntries(
    Object.entries({
      meta_marketing_consent: true,
      meta_fbp: fbp,
      meta_fbc: fbc,
      meta_fbclid: fbclid,
    })
      .map(([key, value]) => [key, cleanMetaAttributionValue(value)])
      .filter(([, value]) => value !== undefined && value !== ""),
  );

  storeMetaAttribution(attribution);
  return attribution;
};

export const getMetaBrowserAttribution = () => {
  if (!isBrowser()) return {};
  const attribution = captureMetaBrowserAttribution();
  return {
    ...attribution,
    meta_event_source_url: window.location.href,
  };
};

const getStoredRedditAttribution = () => {
  if (!isBrowser()) return {};
  try {
    const stored = JSON.parse(localStorage.getItem(REDDIT_ATTRIBUTION_STORAGE_KEY) || "{}");
    if (!stored || typeof stored !== "object") return {};
    if (stored.updated_at && Date.now() - Number(stored.updated_at) > REDDIT_ATTRIBUTION_TTL_MS) {
      localStorage.removeItem(REDDIT_ATTRIBUTION_STORAGE_KEY);
      return {};
    }
    return stored;
  } catch {
    return {};
  }
};

const storeRedditAttribution = (attribution = {}) => {
  if (!isBrowser()) return;
  const persistable = {};
  ["reddit_rdt_cid", "reddit_uuid"].forEach((key) => {
    const value = cleanRedditAttributionValue(attribution[key]);
    if (value !== undefined && value !== "") persistable[key] = value;
  });

  if (!Object.keys(persistable).length) return;

  try {
    localStorage.setItem(REDDIT_ATTRIBUTION_STORAGE_KEY, JSON.stringify({
      ...getStoredRedditAttribution(),
      ...persistable,
      updated_at: Date.now(),
    }));
  } catch {}
};

const getScreenDimensions = () => {
  if (!isBrowser() || !window.screen) return {};
  const width = Number(window.screen.width);
  const height = Number(window.screen.height);
  return {
    reddit_screen_width: Number.isFinite(width) && width > 0 ? String(Math.min(width, 32767)) : undefined,
    reddit_screen_height: Number.isFinite(height) && height > 0 ? String(Math.min(height, 32767)) : undefined,
  };
};

export const captureRedditBrowserAttribution = () => {
  if (!isBrowser()) return {};

  const marketingConsent = isRedditPixelEnabled();
  if (!marketingConsent) {
    return { reddit_marketing_consent: false };
  }

  const stored = getStoredRedditAttribution();
  const rdtCid =
    getUrlRedditParam("rdt_cid")
    || getUrlRedditParam("reddit_rdt_cid")
    || stored.reddit_rdt_cid;
  const uuid = readCookie("_rdt_uuid") || getUrlRedditParam("reddit_uuid") || stored.reddit_uuid;

  const attribution = Object.fromEntries(
    Object.entries({
      reddit_marketing_consent: true,
      reddit_rdt_cid: rdtCid,
      reddit_uuid: uuid,
      ...getScreenDimensions(),
    })
      .map(([key, value]) => [key, cleanRedditAttributionValue(value)])
      .filter(([, value]) => value !== undefined && value !== ""),
  );

  storeRedditAttribution(attribution);
  return attribution;
};

export const getRedditBrowserAttribution = () => {
  if (!isBrowser()) return {};
  const attribution = captureRedditBrowserAttribution();
  return {
    ...attribution,
    reddit_event_source_url: window.location.href,
  };
};

export const sizeToMb = (bytes) => {
  if (!Number.isFinite(bytes)) return undefined;
  return Math.round((bytes / 1024 / 1024) * 10) / 10;
};

export const sizeBucket = (bytes) => {
  if (!Number.isFinite(bytes)) return "unknown";
  const mb = bytes / 1024 / 1024;
  if (mb < 5) return "lt_5mb";
  if (mb < 25) return "5_25mb";
  if (mb < 50) return "25_50mb";
  if (mb < 100) return "50_100mb";
  if (mb < 500) return "100_500mb";
  return "gt_500mb";
};

export const classifyError = (error) => {
  const message = String(error?.message || error || "").toLowerCase();
  if (['request_timeout', 'upload_timeout', 'upload_stalled'].includes(error?.code) || error?.name === "AbortError" || error?.name === "TimeoutError") return "timeout";
  if (error?.code === 'upload_connection_interrupted') return 'network';
  if (error?.code === 'upload_cancelled') return 'cancelled';
  if (error?.name === "NotAllowedError") return "permission";
  if (!message) return "unknown";
  if (message.includes("cancel")) return "cancelled";
  if (message.includes("network") || message.includes("failed to fetch")) return "network";
  if (message.includes("active session") || message.includes("auth")) return "auth";
  if (message.includes("limit") || message.includes("too long") || message.includes("duration")) return "plan_limit";
  if (message.includes("permission") || message.includes("denied") || message.includes("notallowed")) return "permission";
  if (message.includes("timeout")) return "timeout";
  return "application";
};

export const trackEvent = (eventName, params = {}) => {
  if (!analyticsAllowed()) return false;
  ({ eventName, params } = productLimitEvent(eventName, params));
  const gtag = getGtag();
  if (!gtag || !eventName) return false;
  if (params.limit_type && eventName.endsWith('_limit_reached')) rememberCheckoutLimitEvent(eventName, params);
  rememberCheckoutAppEvent(eventName);
  const { source, medium, campaign, ...eventParams } = params;
  try { gtag("event", eventName, cleanParams({
    app_surface: ANALYTICS_SURFACE,
    account_type: accountType,
    ...getAcquisitionContext(),
    ...getGuestAnalyticsContext(),
    ...cleanParams(eventParams),
    app_version: appBuildVersion(),
    ...(source && !eventParams.event_source ? { event_source: source } : {}),
  })); } catch { return false; }
  return true;
};

const diagnosticTimes = new Map();
export const diagnosticErrorCode = error => {
  if (['request_cancelled', 'malformed_response'].includes(error?.code)) return error.code;
  const code = String(error?.code || '').toLowerCase();
  if (UPLOAD_ERROR_CODES.has(code)) return code;
  if (/^(?:youtube_[a-z_]+|invalid_youtube_url|daily_limit_reached|guest_daily_limit|guest_pending_upload_limit|guest_session_expired|guest_session_ended|guest_signup_rate_limit|upload_rate_limited|upload_connection_interrupted|file_too_large|invalid_speaker_name|unknown_speaker|request_timeout|session_not_found|permission_denied|missing_tab_audio)$/.test(code)) return code;
  if (/^(?:[0-9]{5}|pgrst[0-9]{3})$/.test(code)) return `database_${code}`;
  if (error?.name === 'AbortError' || error?.name === 'TimeoutError') return 'request_timeout';
  if (error?.name === 'NotAllowedError') return 'permission_denied';
  if (error?.name === 'NotFoundError') return 'device_not_found';
  const status = Number(error?.status);
  if (Number.isInteger(status) && status >= 400 && status <= 599) return `http_${status}`;
  return classifyError(error) === 'network' ? 'network_failed' : 'request_failed';
};

// Polling and render retries must not inflate error counts. Never send raw
// exception messages, URLs, stacks, filenames, account IDs or request bodies.
export const requestMeasurements = error => ({
  ...(['result_prefetch','result_open','result_refresh','result_download'].includes(error?.event_source)
    ? { event_source: error.event_source } : {}),
  ...(Number.isFinite(error?.elapsed_ms) ? { elapsed_ms: Math.max(0, Math.min(86400000, Math.round(error.elapsed_ms))) } : {}),
  ...Object.fromEntries(['header_ms','body_ms','auth_ms','db_ms','render_ms']
    .filter(name => Number.isFinite(error?.[name]) && error[name] >= 0 && error[name] <= 86400000)
    .map(name => [name, Math.round(error[name] * 10) / 10])),
  ...(['navigation','view_closed','account_changed','replaced','cancelled','timeout','network','http_error','parse'].includes(error?.termination_reason)
    ? { termination_reason: error.termination_reason } : {}),
});

export const trackDiagnostic = (eventName, error, { operation = 'unknown', source, ...params } = {}) => {
  if (error?.code === 'request_cancelled') eventName = 'api_request_cancelled';
  const errorCode = diagnosticErrorCode(error);
  const key = `${eventName}:${operation}:${errorCode}`;
  if (diagnosticTimes.has(key) && Date.now() - diagnosticTimes.get(key) < 60000) return false;
  let details = {};
  try { if (isBrowser()) details = window.instantTranscriberDiagnostics?.describeError(error, { component: params.error_component }) || {}; } catch {}
  const sent = trackEvent(eventName, { ...details, source, operation, error_code: errorCode,
    error_type: classifyError(error),
    ...requestMeasurements(error),
    ...(Number(error?.status) >= 400 ? { status_code: Number(error.status) } : {}), ...params });
  if (sent) {
    if (eventName === 'premium_status_error') trackEvent('client_problem_premium_lookup', {
      operation: 'premium_lookup', error_code: errorCode, error_type: classifyError(error) });
    if (diagnosticTimes.size >= 100) diagnosticTimes.clear();
    diagnosticTimes.set(key, Date.now());
  }
  return sent;
};

export const trackGuestUpgradeStart = (method, userId) => {
  if (!['google', 'apple', 'email', 'existing_account'].includes(method)) return;
  if (method !== 'existing_account') rememberAuthAttempt(method, 'signup');
  const saveContext = guestSaveContext(method);
  if (!trackEvent('guest_upgrade_start', { method, ...saveContext })) return;
  const intent = { method, ...saveContext, expires: Date.now() + 86400000, ...(method === 'email' && userId ? { userId } : {}) };
  try {
    sessionStorage.setItem(GUEST_UPGRADE_KEY, JSON.stringify(intent));
    // Email callbacks often open in a new tab. This is account-bound analytics
    // context, never the bearer proof, address or password.
    if (intent.userId) localStorage.setItem(`${GUEST_UPGRADE_KEY}:${userId}`, JSON.stringify(intent));
  } catch {}
};

const guestUpgradeIntent = user => {
  if (!user || user.is_anonymous || !analyticsAllowed()) return null;
  try {
    const tab = JSON.parse(sessionStorage.getItem(GUEST_UPGRADE_KEY) || 'null');
    const intent = tab?.expires > Date.now() && (!tab.userId || tab.userId === user.id) ? tab
      : JSON.parse(localStorage.getItem(`${GUEST_UPGRADE_KEY}:${user.id}`) || 'null');
    return intent?.expires > Date.now() && ['google', 'apple', 'email', 'existing_account'].includes(intent.method) ? intent : null;
  } catch { return null; }
};

const trackGuestUpgradeStage = (user, event, stage) => {
  const intent = guestUpgradeIntent(user);
  if (!intent) return false;
  const key = `${GUEST_UPGRADE_KEY}:${user.id}:${stage}`;
  try { if (localStorage.getItem(key)) return false; } catch {}
  if (!trackEvent(event, { method: intent.method, guest_stage: stage, account_type: 'registered',
    ...(SAVE_LOCATIONS.has(intent.cta_location) ? { cta_location: intent.cta_location,
      offer_variant: ['result_context_v3','result_context_v4'].includes(intent.offer_variant) ? intent.offer_variant : 'guest_save_v2' } : {}) })) return false;
  try { localStorage.setItem(key, String(Date.now())); } catch {}
  return true;
};

export const trackGuestUpgradeAccountVerified = user => trackGuestUpgradeStage(user, 'guest_upgrade_account_verified', 'account_verified');
export const trackGuestUpgradeHistorySaved = user => trackGuestUpgradeStage(user, 'guest_upgrade_history_saved', 'history_saved');
export const trackGuestUpgradeCredentialsReady = user => {
  const tracked = trackGuestUpgradeStage(user, 'guest_upgrade_password_saved', 'credentials_ready');
  trackGuestUpgradeCompleted(user);
  return tracked;
};

export const trackGuestUpgradeCompleted = (user) => {
  const intent = guestUpgradeIntent(user);
  if (!intent) return false;
  try {
    // A confirmed email without a password is not a finished email signup.
    if (intent.method === 'email' && !localStorage.getItem(`${GUEST_UPGRADE_KEY}:${user.id}:credentials_ready`)) return false;
  } catch { return false; }
  const tracked = trackGuestUpgradeStage(user, 'guest_upgrade_completed', 'completed');
  if (tracked) try { sessionStorage.removeItem(GUEST_UPGRADE_KEY); } catch {}
  return tracked;
};

const eventSuffix = (value) => {
  const cleaned = cleanValue(value);
  if (!cleaned) return "";
  return String(cleaned)
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

export const trackEventWithCta = (eventName, params = {}) => {
  const tracked = trackEvent(eventName, params);

  const suffix = eventSuffix(params.checkout_cta_location || params.cta_location);
  if (!suffix || !eventName) return tracked;

  const prefix = `${eventName}_`;
  if (prefix.length >= MAX_GA_EVENT_NAME_LENGTH) return tracked;

  const ctaEventName = `${prefix}${suffix.slice(0, MAX_GA_EVENT_NAME_LENGTH - prefix.length)}`;
  if (ctaEventName !== eventName) {
    trackEvent(ctaEventName, params);
  }
  return tracked;
};

const getEventStorage = () => {
  if (!isBrowser()) return null;
  try {
    return window.localStorage || window.sessionStorage;
  } catch {
    return null;
  }
};

const getSessionEventStorage = () => {
  if (!isBrowser()) return null;
  try {
    return window.sessionStorage || window.localStorage;
  } catch {
    return null;
  }
};

const hasStorageKey = (storage, key) => {
  if (!storage || !key) return false;
  try {
    return Boolean(storage.getItem(key));
  } catch {
    return false;
  }
};

const markStorageKey = (storage, key) => {
  if (!storage || !key) return;
  try {
    storage.setItem(key, String(Date.now()));
  } catch {}
};

export const trackSignupCompleted = (params = {}, userId) => {
  const method = cleanValue(params.method) || "unknown";
  const userKey = cleanValue(userId) || `anonymous:${method}`;
  const storage = getEventStorage();
  const storageKey = `${GA_SIGNUP_EVENT_STORAGE_PREFIX}${userKey}`;
  if (hasStorageKey(storage, storageKey)) return false;

  const gtag = getGtag();
  if (gtag && userId) gtag("set", { user_id: cleanValue(userId) });
  const payload = cleanParams({
    ...params,
    method,
    user_id_present: Boolean(userId),
  });
  const didTrackSignUp = trackEvent("sign_up", payload);
  const didTrackSignupSuccess = trackEvent("signup_success", payload);
  if (didTrackSignUp || didTrackSignupSuccess) markStorageKey(storage, storageKey);
  return didTrackSignUp || didTrackSignupSuccess;
};

const trackMetaStandardEvent = (eventName, params = {}) => {
  if (!eventName || !isMetaPixelEnabled()) return false;

  const fbq = getFbq();
  if (!fbq) return false;

  const eventId = cleanValue(params.event_id || params.eventID);
  const eventParams = { ...params };
  delete eventParams.event_id;
  delete eventParams.eventID;
  if (eventId) {
    fbq("track", eventName, cleanParams(eventParams), { eventID: eventId });
  } else {
    fbq("track", eventName, cleanParams(eventParams));
  }
  return true;
};

const trackMetaCustomEvent = (eventName, params = {}) => {
  if (!eventName || !isMetaPixelEnabled()) return false;

  const fbq = getFbq();
  if (!fbq) return false;

  const eventId = cleanValue(params.event_id || params.eventID);
  const eventParams = { ...params };
  delete eventParams.event_id;
  delete eventParams.eventID;
  if (eventId) {
    fbq("trackCustom", eventName, cleanParams(eventParams), { eventID: eventId });
  } else {
    fbq("trackCustom", eventName, cleanParams(eventParams));
  }
  return true;
};

const trackRedditStandardEvent = (eventName, params = {}) => {
  if (!eventName || !isRedditPixelEnabled()) return false;

  const rdt = getRdt();
  if (!rdt) return false;

  const eventParams = cleanParams(params);
  if (Object.keys(eventParams).length) {
    rdt("track", eventName, eventParams);
  } else {
    rdt("track", eventName);
  }
  return true;
};

const trackRedditCustomEvent = (eventName, params = {}) => {
  return trackRedditStandardEvent("Custom", {
    customEventName: eventName,
    ...params,
  });
};

export const trackProductIntentEvent = (eventName, params = {}, options = {}) => {
  if (!eventName) return { meta: false, reddit: false };
  if (params.limit_type || String(eventName).includes("limit_reached")) {
    rememberCheckoutLimitEvent(eventName, params);
  }

  const storage = options.sessionOnly ? getSessionEventStorage() : getEventStorage();
  const dedupeKey = cleanValue(options.dedupeKey);
  const userKey = cleanValue(options.userId) || "anonymous";
  const storageKey = dedupeKey
    ? `${PRODUCT_INTENT_EVENT_STORAGE_PREFIX}${eventName}:${userKey}:${dedupeKey}`
    : null;

  if (storageKey && hasStorageKey(storage, storageKey)) {
    return { meta: false, reddit: false, deduped: true };
  }

  const payload = cleanParams({
    event_category: "product_intent",
    product_area: "transcription",
    ...params,
  });
  const meta = trackMetaCustomEvent(eventName, payload);
  const reddit = trackRedditCustomEvent(eventName, payload);

  if (storageKey && (meta || reddit)) {
    markStorageKey(storage, storageKey);
  }

  return { meta, reddit };
};

export const trackMetaLead = (params = {}) => {
  const key = `${META_EVENT_STORAGE_PREFIX}Lead:signup`;
  const storage = getSessionEventStorage();
  if (hasStorageKey(storage, key)) return false;

  const didTrack = trackMetaStandardEvent("Lead", {
    content_name: "signup",
    ...params,
  });
  if (didTrack) markStorageKey(storage, key);
  return didTrack;
};

export const trackRedditLead = () => {
  const key = `${REDDIT_EVENT_STORAGE_PREFIX}Lead:signup`;
  const storage = getSessionEventStorage();
  if (hasStorageKey(storage, key)) return false;

  const didTrack = trackRedditStandardEvent("Lead");
  if (didTrack) markStorageKey(storage, key);
  return didTrack;
};

export const trackMetaCompleteRegistration = (params = {}, userId) => {
  const key = userId
    ? `${META_EVENT_STORAGE_PREFIX}CompleteRegistration:${userId}`
    : `${META_EVENT_STORAGE_PREFIX}CompleteRegistration:anonymous`;
  const storage = getEventStorage();
  if (hasStorageKey(storage, key)) return false;

  const didTrack = trackMetaStandardEvent("CompleteRegistration", {
    content_name: "account",
    status: "created",
    ...params,
  });
  if (didTrack) markStorageKey(storage, key);
  return didTrack;
};

export const trackRedditSignUp = (params = {}, userId) => {
  const key = userId
    ? `${REDDIT_EVENT_STORAGE_PREFIX}SignUp:${userId}`
    : `${REDDIT_EVENT_STORAGE_PREFIX}SignUp:anonymous`;
  const storage = getEventStorage();
  if (hasStorageKey(storage, key)) return false;

  const didTrack = trackRedditStandardEvent("SignUp");
  if (didTrack) markStorageKey(storage, key);
  return didTrack;
};

export const trackMetaInitiateCheckout = (params = {}) => {
  return trackMetaStandardEvent("InitiateCheckout", {
    content_name: "premium_subscription",
    currency: META_DEFAULT_CURRENCY,
    value: params.billing_interval === 'annual' ? pricing.annualProTotalPrice : pricing.monthlyProPrice,
    ...params,
  });
};

const trackMetaPurchase = (params = {}) => {
  return trackMetaStandardEvent("Purchase", {
    content_name: "premium_subscription",
    ...params,
  });
};

const trackRedditPurchase = (params = {}) => {
  const transactionId = cleanValue(params.transaction_id || params.checkout_session_id);
  if (!transactionId) return false;

  return trackRedditStandardEvent("Purchase", {
    currency: params.currency,
    value: params.value,
    itemCount: 1,
    transactionId,
    conversionId: transactionId,
  });
};

export const trackUpgradeClick = (params = {}) => {
  const payload = cleanParams({
    conversion_target: "premium",
    ...params,
  });
  trackEventWithCta("upgrade_click", payload);
  trackEventWithCta("select_promotion", {
    ...payload,
    promotion_name: "premium_subscription",
    creative_name: payload.cta_location,
    creative_slot: payload.checkout_cta_location || payload.cta_location,
  });
};

export const trackPremiumActivated = (params = {}) => {
  const transactionId = params.transaction_id || params.checkout_session_id;
  if (typeof transactionId !== 'string' || !/^cs_[A-Za-z0-9_]{1,200}$/.test(transactionId)
      || params.checkout_verified !== true || params.paid !== true
      || typeof params.value !== 'number' || !Number.isFinite(params.value) || params.value <= 0
      || !/^[A-Z]{3}$/.test(params.currency)) return false;
  const storage = getEventStorage();
  const analyticsParams = { ...params, checkout_verified: undefined, paid: undefined,
    amount_total: undefined, created_at: undefined };
  const key = `${PREMIUM_ACTIVATION_STORAGE_PREFIX}${transactionId}`;
  // Old clients used one marker for every destination. Respect it on upgrades.
  if (hasStorageKey(storage, key)) return false;
  let tracked = false;
  const once = (destination, send) => {
    if (hasStorageKey(storage, `${key}:${destination}`)) return;
    if (send()) { markStorageKey(storage, `${key}:${destination}`); tracked = true; }
  };
  once('ga', () => trackEventWithCta("premium_activated", {
    conversion_type: "subscription",
    billing_provider: "stripe",
    ...analyticsParams,
    transaction_id: transactionId,
    checkout_session_id: undefined,
  }));
  once('meta', () => trackMetaPurchase({
    billing_provider: "stripe",
    status: "active",
    source: cleanValue(params.source),
    event_id: transactionId,
    currency: params.currency,
    value: params.value,
  }));
  once('reddit', () => trackRedditPurchase({
    transaction_id: transactionId,
    currency: params.currency,
    value: params.value,
  }));
  return tracked;
};

export const trackPageView = (pagePath, params = {}) => {
  if (!isBrowser()) return;
  // The parent landing page already owns this visit and the GA session.
  if (embeddedParent()) return;
  const path = pagePath || `${window.location.pathname}${window.location.search}`;
  captureMetaBrowserAttribution();
  captureRedditBrowserAttribution();
  trackMetaPageView(path);
  trackRedditPageVisit(path);

  const gtag = getGtag();
  if (!gtag || !analyticsAllowed()) return;
  gtag("event", "page_view", cleanParams({
    app_surface: ANALYTICS_SURFACE,
    page_path: path,
    page_title: document.title,
    page_location: `${window.location.origin}${path}`,
    ...params,
    app_version: appBuildVersion(),
  }));
};

export const trackMetaPageView = (pagePath) => {
  if (!isMetaPixelEnabled()) return;

  const fbq = getFbq();
  if (!fbq) return;

  const path = pagePath || `${window.location.pathname}${window.location.search}`;
  if (window.__instantTranscriberMetaPixelLastPageViewPath === path) return;

  window.__instantTranscriberMetaPixelLastPageViewPath = path;
  fbq("track", "PageView");
};

export const trackRedditPageVisit = (pagePath) => {
  if (!isRedditPixelEnabled()) return;

  const rdt = getRdt();
  if (!rdt) return;

  const path = pagePath || `${window.location.pathname}${window.location.search}`;
  if (window.__instantTranscriberRedditPixelLastPageVisitPath === path) return;

  window.__instantTranscriberRedditPixelLastPageVisitPath = path;
  rdt("track", "PageVisit");
};

export const setAnalyticsUser = (user, properties = {}) => {
  accountType = !user ? 'visitor' : user.is_anonymous ? 'guest' : 'registered';
  const gtag = getGtag();
  if (!gtag || !analyticsAllowed()) return;
  try {
    gtag("set", { user_id: user?.id || null });
    gtag("set", "user_properties", cleanParams({
      account_plan: properties.account_plan,
      auth_provider: properties.auth_provider,
      account_type: accountType,
    }));
  } catch {}
};

export const trackWebVital = (metric) => {
  if (!metric) return;
  const value = metric.name === "CLS"
    ? Math.round(metric.value * 1000)
    : Math.round(metric.value);

  trackEvent("web_vital", {
    event_category: "Web Vitals",
    metric_name: metric.name,
    metric_id: metric.id,
    value,
    non_interaction: true,
  });
};
